import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

/**
 * Get inventory item by inventory_item_id (for QR scanning)
 */
export const getInventoryItemByInventoryItemId = createAsyncThunk(
  "StockTransfer/getInventoryItemByInventoryItemId",
  async (
    {
      inventory_item_id,
      warehouse_id,
    }: {
      inventory_item_id: string;
      warehouse_id?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (warehouse_id) {
        params.append("warehouse_id", warehouse_id);
      }

      const url = `${(api.Inventory.dispatch as any).stock_transfer.inventory_item_by_id}${inventory_item_id}${params.toString() ? `?${params.toString()}` : ""}`;
      
      const response: any = await Axios.get(url);
      
      if (response?.data?.success && response?.data?.data) {
        return response.data.data;
      }
      
      return rejectWithValue(
        response?.data?.message || response?.data?.error || "Failed to fetch inventory item"
      );
    } catch (error: any) {
      // Extract error message from various possible locations
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to fetch inventory item. Please try again.";
      
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Create stock transfer dispatch
 */
export const createStockTransferDispatch = createAsyncThunk(
  "StockTransfer/createStockTransferDispatch",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        (api.Inventory.dispatch as any).stock_transfer.create,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }
      
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        "Failed to create stock transfer dispatch";
      
      return rejectWithValue(errorMessage);
    } catch (error: any) {
      const responseData = error?.response?.data || {};
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        "Failed to create stock transfer dispatch. Please try again.";
      
      // Extract validation errors if present
      const validationErrors = responseData?.data?.errors || responseData?.errors;
      
      return rejectWithValue({
        message: errorMessage,
        errors: validationErrors,
        responseData: responseData,
      });
    }
  }
);

/**
 * Update stock transfer dispatch
 */
export const updateStockTransferDispatch = createAsyncThunk(
  "StockTransfer/updateStockTransferDispatch",
  async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `${(api.Inventory.dispatch as any).stock_transfer.update}${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }
      
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        "Failed to update stock transfer dispatch";
      
      return rejectWithValue(errorMessage);
    } catch (error: any) {
      const responseData = error?.response?.data || {};
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        error?.message ||
        "Failed to update stock transfer dispatch. Please try again.";
      
      // Extract validation errors if present
      const validationErrors = responseData?.data?.errors || responseData?.errors;
      
      return rejectWithValue({
        message: errorMessage,
        errors: validationErrors,
        responseData: responseData,
      });
    }
  }
);


interface ExportFilters {
  search?: string;
  warehouse_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any; // any extra filters
}

export const fetchStockTransfer = createAsyncThunk(
  "StockTransfer/fetchStockTransfer",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouse_id: string;
  }) => {
    console.log("FILTER VALUE RECEIVED BY API CALL:", filters);
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());

    if (search) params.append("search", search);

    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    // Helper
    const formatDate = (date: any) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0]; // ALWAYS YYYY-MM-DD, no timezone issues
    };

    // Apply filters
    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Handle date-range filters (object with from_date / to_date)
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            // Created / Updated / Order Date → same pattern as ORDER API
            if (
              from_date &&
              (filterField === "created_at" ||
                filterField === "updated_at" ||
                filterField === "dispatch_date")
            ) {
             params.append(`${filterField}_from`, formatDate(from_date));


            }

            if (
              to_date &&
              (filterField === "created_at" ||
                filterField === "updated_at" ||
                filterField === "dispatch_date")
            ) {
             params.append(`${filterField}_to`, formatDate(to_date));
            }

            return; // Stop processing here
          }

          // 🔤 Simple filters (string, number)
          params.append(filterField, String(filterValue));
        }
      );
    }
    // API CALL
    const response: any = await Axios.get(
      `${api.Inventory.dispatch.stock_transfer.listing
      }?warehouse_id=${warehouse_id}&${params.toString()}`
    );

    return {
      data: response?.data?.data || [],
      rowCount: response?.data?.count || 0,
    };
  }
);

// Get By Stock Transfer BY ID
// 🔍 GET Stock Transfer Details by ID
export const fetchStockTransferById = createAsyncThunk(
  "StockTransfer/fetchStockTransferById",
  async (dispatch_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${api.Inventory.dispatch.stock_transfer.getByID}/${dispatch_id}`
      );

      return response?.data?.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch stock transfer details"
      );
    }
  }
);



// CSV Export 
// export const exportReadyForDispatchCSV = async (filters: ExportFilters = {}) => {
//   // Build query parameters from filters
//   const params = new URLSearchParams({ export: "csv", ...filters } as any);

//   const response = await Axios.get(
//     `${api.Inventory.dispatch.ready_for_dispatch.export_csv}?${params.toString()}`,
//     {
//       responseType: "blob",
//     }
//   );

//   return {
//     blob: response.data,
//     filename:
//       response.headers["content-disposition"]
//         ?.split("filename=")[1]
//         ?.replace(/"/g, "") || "readyForDispatch.csv",
//   };
// };
